import { createReducer, on } from '@ngrx/store';
import { PixelFrame, createBlankFrame, celKey, CelPixels } from '@models/frame.model';
import { SpriteLayer, createBlankSpriteLayer, LayerWithPixels, compositeLayers } from '@models/layer.model';
import { FrameActions } from '@store/actions/frame.actions';
import { defaultSetting } from '@models/settings.model';
import { generateUUID } from '@app/lib/utility/helper';

const MAX_HISTORY = 100;

// ── State ────────────────────────────────────────────────────────────────────

export interface FrameState {
  frames:        PixelFrame[];
  /** Global ordered layer stack (same across all frames). */
  layers:        SpriteLayer[];
  /** Sparse cel map: key = `${frameId}|${layerId}`, value = pixel grid. */
  cels:          Record<string, CelPixels>;
  activeLayerId: string | null;
  currentFrameId: string | null;
  isLoading: boolean;
  error: any;
  past:   FrameSnapshot[];
  future: FrameSnapshot[];
}

export interface FrameSnapshot {
  frames:        PixelFrame[];
  layers:        SpriteLayer[];
  cels:          Record<string, CelPixels>;
  activeLayerId: string | null;
  currentFrameId: string | null;
}

// ── Initial state ─────────────────────────────────────────────────────────────

const W = defaultSetting.Resolution.width;
const H = defaultSetting.Resolution.height;

const _initialLayer = createBlankSpriteLayer('Layer 1');
const _initialFrame = createBlankFrame(W, H);

const initialState: FrameState = {
  frames:        [_initialFrame],
  layers:        [_initialLayer],
  cels:          {},    // blank canvas → no cels needed (composite is all transparent)
  activeLayerId: _initialLayer.id,
  currentFrameId: _initialFrame.id,
  isLoading: false,
  error: null,
  past:   [],
  future: [],
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function snapshot(state: FrameState): FrameState {
  const snap: FrameSnapshot = {
    frames:        state.frames,       // immutable reference — O(1)
    layers:        state.layers,
    cels:          state.cels,
    activeLayerId: state.activeLayerId,
    currentFrameId: state.currentFrameId,
  };
  return { ...state, past: [snap, ...state.past].slice(0, MAX_HISTORY), future: [] };
}

function capHistory(h: FrameSnapshot[] | undefined): FrameSnapshot[] {
  return (h ?? []).slice(0, MAX_HISTORY);
}

function frameDims(frame: PixelFrame): { w: number; h: number } {
  return { h: frame.pixels.length, w: frame.pixels[0]?.length ?? 0 };
}

/** Get cel pixels or a transparent blank of the right size. */
function getCelPixels(
  cels: Record<string, CelPixels>,
  frameId: string, layerId: string,
  w: number, h: number
): CelPixels {
  return cels[celKey(frameId, layerId)] ?? Array.from({ length: h }, () => Array(w).fill('#00000000'));
}

/** Build the LayerWithPixels[] for compositing one frame. */
function layersForFrame(
  layers: SpriteLayer[],
  cels: Record<string, CelPixels>,
  frameId: string,
  w: number, h: number
): LayerWithPixels[] {
  return layers.map(l => ({
    ...l,
    pixels: getCelPixels(cels, frameId, l.id, w, h),
  }));
}

/** Recomposite a single frame and return an updated PixelFrame. */
function recomposite(
  frame: PixelFrame,
  layers: SpriteLayer[],
  cels: Record<string, CelPixels>
): PixelFrame {
  const { w, h } = frameDims(frame);
  if (!w || !h) return frame;
  const lp = layersForFrame(layers, cels, frame.id, w, h);
  return { ...frame, pixels: compositeLayers(lp, w, h) };
}

/** Recomposite all frames. */
function recompositeAll(
  frames: PixelFrame[],
  layers: SpriteLayer[],
  cels: Record<string, CelPixels>
): PixelFrame[] {
  return frames.map(f => recomposite(f, layers, cels));
}

/** Apply a paint function to the active layer's cel (auto-creates if absent). */
function paintCel(
  state: FrameState,
  frameId: string,
  applyFn: (pixels: CelPixels) => CelPixels
): FrameState {
  const layer = state.layers.find(l => l.id === state.activeLayerId) ?? state.layers[state.layers.length - 1];
  if (!layer || layer.locked) return state;
  const frame = state.frames.find(f => f.id === frameId);
  if (!frame) return state;
  const { w, h } = frameDims(frame);
  const key = celKey(frameId, layer.id);
  const existing = getCelPixels(state.cels, frameId, layer.id, w, h);
  const newPixels = applyFn(existing);
  const cels = { ...state.cels, [key]: newPixels };
  const updatedFrame = recomposite(frame, state.layers, cels);
  return { ...state, cels, frames: state.frames.map(f => f.id === frameId ? updatedFrame : f) };
}

// ── Flood fill ────────────────────────────────────────────────────────────────

function floodFill(pixels: string[][], row: number, col: number, target: string, fill: string): string[][] {
  if (target === fill) return pixels;
  const rows = pixels.length, cols = pixels[0].length;
  const np = pixels.map(r => [...r]);
  const stack: [number, number][] = [[row, col]];
  while (stack.length) {
    const [r, c] = stack.pop()!;
    if (r < 0 || r >= rows || c < 0 || c >= cols || np[r][c] !== target) continue;
    np[r][c] = fill;
    stack.push([r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]);
  }
  return np;
}

// ── Color helpers ─────────────────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] {
  const h = (hex || '#000000').replace('#', '').padEnd(6, '0');
  return [parseInt(h.slice(0, 2), 16) || 0, parseInt(h.slice(2, 4), 16) || 0, parseInt(h.slice(4, 6), 16) || 0];
}
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
}
function lerpColor(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a), [br, bg, bb] = hexToRgb(b);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

// ── Reducer ───────────────────────────────────────────────────────────────────

export const frameReducer = createReducer(
  initialState,

  // ── Frame CRUD ──────────────────────────────────────────────────────────────
  on(FrameActions.addBlankFrame, (state) => {
    const ref = state.frames[0];
    const h = ref ? ref.pixels.length : H;
    const w = ref ? (ref.pixels[0]?.length ?? W) : W;
    const f = createBlankFrame(w, h);
    return { ...snapshot(state), frames: [...state.frames, f], currentFrameId: f.id };
  }),

  on(FrameActions.duplicateFrame, (state, { frameId }) => {
    const idx = state.frames.findIndex(f => f.id === frameId);
    if (idx === -1) return state;
    const src = state.frames[idx];
    const newId = generateUUID();
    // Deep-copy all cels for this frame
    const newCels = { ...state.cels };
    for (const layer of state.layers) {
      const srcKey = celKey(frameId, layer.id);
      if (state.cels[srcKey]) {
        newCels[celKey(newId, layer.id)] = state.cels[srcKey].map(r => [...r]);
      }
    }
    const newFrame: PixelFrame = { id: newId, pixels: src.pixels.map(r => [...r]) };
    const frames = [...state.frames];
    frames.splice(idx + 1, 0, newFrame);
    return { ...state, frames, cels: newCels, currentFrameId: newId };
  }),

  on(FrameActions.deleteFrame, (state, { frameId }) => {
    if (state.frames.length <= 1) return state;
    const idx = state.frames.findIndex(f => f.id === frameId);
    const frames = state.frames.filter(f => f.id !== frameId);
    // Remove all cels for this frame
    const prefix = frameId + '|';
    const cels = Object.fromEntries(Object.entries(state.cels).filter(([k]) => !k.startsWith(prefix)));
    const cur = state.currentFrameId === frameId
      ? (frames[Math.max(0, idx - 1)]?.id ?? frames[0].id)
      : state.currentFrameId;
    return { ...snapshot(state), frames, cels, currentFrameId: cur };
  }),

  on(FrameActions.selectFrame, (state, { frameId }) => ({ ...state, currentFrameId: frameId })),

  on(FrameActions.clearFrame, (state, { frameId }) => {
    const snap = snapshot(state);
    const frame = snap.frames.find(f => f.id === frameId);
    if (!frame) return snap;
    const { w, h } = frameDims(frame);
    // Remove all cels for this frame
    const prefix = frameId + '|';
    const cels = Object.fromEntries(Object.entries(snap.cels).filter(([k]) => !k.startsWith(prefix)));
    const blank = Array.from({ length: h }, () => Array(w).fill('#00000000'));
    const frames = snap.frames.map(f => f.id === frameId ? { ...f, pixels: blank } : f);
    return { ...snap, frames, cels };
  }),

  on(FrameActions.clearCel, (state, { frameId, layerId }) => {
    const snap = snapshot(state);
    const key = celKey(frameId, layerId);
    if (!(key in snap.cels)) return snap; // already empty – nothing to do
    // Remove just this one cel
    const cels = Object.fromEntries(Object.entries(snap.cels).filter(([k]) => k !== key));
    // Recomposite only the affected frame
    const frame = snap.frames.find(f => f.id === frameId);
    const frames = frame
      ? snap.frames.map(f => f.id === frameId ? recomposite(f, snap.layers, cels) : f)
      : snap.frames;
    return { ...snap, cels, frames };
  }),

  on(FrameActions.moveFrame, (state, { fromIndex, toIndex }) => {
    const frames = [...state.frames];
    const [m] = frames.splice(fromIndex, 1);
    frames.splice(toIndex, 0, m);
    return { ...state, frames };
  }),

  on(FrameActions.setFrames, (state, { frames, layers, cels, activeLayerId }) => {
    const newLayers = layers ?? state.layers;
    const newCels   = cels   ?? state.cels;
    const newActive = activeLayerId !== undefined ? activeLayerId : (newLayers[0]?.id ?? null);
    return {
      ...state,
      frames,
      layers: newLayers,
      cels: newCels,
      activeLayerId: newActive,
      currentFrameId: frames[0]?.id ?? null,
      past: [], future: [],
    };
  }),

  on(FrameActions.restoreFrameState, (state, { frames, layers, cels, activeLayerId, currentFrameId, past, future }) => ({
    ...state,
    frames,
    layers,
    cels,
    activeLayerId,
    currentFrameId: currentFrameId ?? frames[0]?.id ?? null,
    past: capHistory(past),
    future: capHistory(future),
    isLoading: false,
    error: null,
  })),

  on(FrameActions.appendFrames, (state, { frames, cels }) => {
    const snap = snapshot(state);
    const newCels = cels ? { ...snap.cels, ...cels } : snap.cels;
    return {
      ...snap,
      frames: [...snap.frames, ...frames],
      cels: newCels,
      currentFrameId: frames[0]?.id ?? snap.currentFrameId,
    };
  }),

  on(FrameActions.tweenFrames, (state, { fromFrameId, toFrameId, steps }) => {
    const fi = state.frames.findIndex(f => f.id === fromFrameId);
    const ti = state.frames.findIndex(f => f.id === toFrameId);
    if (fi === -1 || ti === -1 || steps < 1) return state;
    const fromF = state.frames[fi], toF = state.frames[ti];
    const { h, w } = frameDims(fromF);
    const tweened: PixelFrame[] = [];
    const newCels: Record<string, CelPixels> = {};
    const bottomLayer = state.layers[0];
    for (let s = 1; s <= steps; s++) {
      const t = s / (steps + 1);
      const pixels: string[][] = Array.from({ length: h }, (_, r) =>
        Array.from({ length: w }, (_, c) => lerpColor(fromF.pixels[r]?.[c] ?? '#000', toF.pixels[r]?.[c] ?? '#000', t))
      );
      const newId = generateUUID();
      tweened.push({ id: newId, pixels });
      if (bottomLayer) newCels[celKey(newId, bottomLayer.id)] = pixels;
    }
    const frames = [...state.frames];
    frames.splice(Math.min(fi, ti) + 1, 0, ...tweened);
    return { ...snapshot(state), frames, cels: { ...state.cels, ...newCels } };
  }),

  // ── Resize canvas ───────────────────────────────────────────────────────────
  on(FrameActions.resizeCanvas, (state, { width, height }) => {
    const frames = state.frames.map(f => {
      const { h: oldH, w: oldW } = frameDims(f);
      if (oldW === width && oldH === height) return f;
      const newPixels: string[][] = Array.from({ length: height }, (_, r) =>
        Array.from({ length: width }, (_, c) => (r < oldH && c < oldW) ? f.pixels[r][c] : '#00000000')
      );
      return { ...f, pixels: newPixels };
    });
    // Resize every cel
    const cels: Record<string, CelPixels> = {};
    for (const [key, cel] of Object.entries(state.cels)) {
      const oldH = cel.length, oldW = cel[0]?.length ?? 0;
      cels[key] = Array.from({ length: height }, (_, r) =>
        Array.from({ length: width }, (_, c) => (r < oldH && c < oldW) ? cel[r][c] : '#00000000')
      );
    }
    return { ...state, frames, cels };
  }),

  // ── Pixel painting ─────────────────────────────────────────────────────────

  on(FrameActions.beginStroke, (state) => snapshot(state)),
  on(FrameActions.commitStroke, (state) => state),

  on(FrameActions.paintPixel, (state, { frameId, row, col, color }) =>
    paintCel(state, frameId, px => {
      const n = px.map(r => [...r]);
      n[row][col] = color;
      return n;
    })
  ),

  on(FrameActions.paintPixels, (state, { frameId, cells }) =>
    paintCel(state, frameId, px => {
      const n = px.map(r => [...r]);
      for (const { row, col, color } of cells) {
        if (row >= 0 && row < n.length && col >= 0 && col < n[0].length) n[row][col] = color;
      }
      return n;
    })
  ),

  on(FrameActions.fillPixels, (state, { frameId, row, col, color }) => {
    const snap = snapshot(state);
    return paintCel(snap, frameId, px => {
      const target = px[row]?.[col];
      return target ? floodFill(px, row, col, target, color) : px;
    });
  }),

  on(FrameActions.fillPixelsSymmetry, (state, { frameId, points, color }) => {
    const snap = snapshot(state);
    return paintCel(snap, frameId, px => {
      let np = px.map(r => [...r]);
      for (const pt of points) {
        const target = np[pt.row]?.[pt.col];
        if (target) np = floodFill(np, pt.row, pt.col, target, color);
      }
      return np;
    });
  }),

  on(FrameActions.replaceFramePixels, (state, { frameId, pixels }) => {
    const snap = snapshot(state);
    const layer = snap.layers.find(l => l.id === snap.activeLayerId) ?? snap.layers[snap.layers.length - 1];
    if (!layer) return snap;
    const key = celKey(frameId, layer.id);
    const cels = { ...snap.cels, [key]: pixels.map(r => [...r]) };
    const frame = snap.frames.find(f => f.id === frameId)!;
    const updatedFrame = recomposite(frame, snap.layers, cels);
    return { ...snap, cels, frames: snap.frames.map(f => f.id === frameId ? updatedFrame : f) };
  }),

  on(FrameActions.replaceFramePixelsLive, (state, { frameId, pixels }) => {
    const layer = state.layers.find(l => l.id === state.activeLayerId) ?? state.layers[state.layers.length - 1];
    if (!layer) return state;
    const key = celKey(frameId, layer.id);
    const cels = { ...state.cels, [key]: pixels.map(r => [...r]) };
    const frame = state.frames.find(f => f.id === frameId)!;
    const updatedFrame = recomposite(frame, state.layers, cels);
    return { ...state, cels, frames: state.frames.map(f => f.id === frameId ? updatedFrame : f) };
  }),

  // Collab: write composite directly (no layer compositing – avoids pixel corruption)
  on(FrameActions.syncRemoteFrame, (state, { frameId, pixels }) => ({
    ...state,
    frames: state.frames.map(f =>
      f.id === frameId ? { ...f, pixels: pixels.map(r => [...r]) } : f
    ),
  })),

  // ── Selection operations ────────────────────────────────────────────────────

  on(FrameActions.applySelectionPaste, (state, { frameId, pixels, destRow, destCol }) => {
    const snap = snapshot(state);
    return paintCel(snap, frameId, lp => {
      const np = lp.map(r => [...r]);
      for (let r = 0; r < pixels.length; r++) for (let c = 0; c < pixels[r].length; c++) {
        const dr = destRow + r, dc = destCol + c;
        if (dr >= 0 && dr < np.length && dc >= 0 && dc < np[0].length) {
          const px = pixels[r][c] ?? '#00000000';
          if (px !== '#00000000' && px !== 'transparent') np[dr][dc] = px;
        }
      }
      return np;
    });
  }),

  on(FrameActions.applySelectionFill, (state, { frameId, r1, c1, r2, c2, color }) => {
    const snap = snapshot(state);
    return paintCel(snap, frameId, lp => {
      const np = lp.map(r => [...r]);
      for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++)
        if (r >= 0 && r < np.length && c >= 0 && c < np[0].length) np[r][c] = color;
      return np;
    });
  }),

  on(FrameActions.cutSelection, (state, { frameId, r1, c1, r2, c2 }) => {
    const snap = snapshot(state);
    return paintCel(snap, frameId, lp => {
      const np = lp.map(r => [...r]);
      for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++)
        if (r >= 0 && r < np.length && c >= 0 && c < np[0].length) np[r][c] = '#00000000';
      return np;
    });
  }),

  on(FrameActions.applySelectionTransform, (state, { frameId, origin, pixels, destRow, destCol }) => {
    const snap = snapshot(state);
    return paintCel(snap, frameId, lp => {
      const np = lp.map(r => [...r]);
      for (let r = origin.r1; r <= origin.r2; r++)
        for (let c = origin.c1; c <= origin.c2; c++)
          if (r >= 0 && r < np.length && c >= 0 && c < np[0].length) np[r][c] = '#00000000';
      for (let r = 0; r < pixels.length; r++) {
        for (let c = 0; c < (pixels[r]?.length ?? 0); c++) {
          const dr = destRow + r, dc = destCol + c;
          if (dr >= 0 && dr < np.length && dc >= 0 && dc < np[0].length) {
            const px = pixels[r][c] ?? '#00000000';
            if (px !== '#00000000' && px !== 'transparent') np[dr][dc] = px;
          }
        }
      }
      return np;
    });
  }),

  // ── Layer operations ─────────────────────────────────────────────────────────

  on(FrameActions.addLayer, (state) => {
    const snap = snapshot(state);
    const nl = createBlankSpriteLayer(`Layer ${snap.layers.length + 1}`);
    return { ...snap, layers: [...snap.layers, nl], activeLayerId: nl.id };
  }),

  on(FrameActions.deleteLayer, (state, { layerId }) => {
    if (state.layers.length <= 1) return state;
    const snap = snapshot(state);
    const layers = snap.layers.filter(l => l.id !== layerId);
    // Remove all cels for this layer
    const suffix = '|' + layerId;
    const cels = Object.fromEntries(Object.entries(snap.cels).filter(([k]) => !k.endsWith(suffix)));
    const frames = recompositeAll(snap.frames, layers, cels);
    const active = snap.activeLayerId === layerId
      ? (layers[layers.length - 1]?.id ?? null)
      : snap.activeLayerId;
    return { ...snap, layers, cels, frames, activeLayerId: active };
  }),

  on(FrameActions.selectLayer, (state, { layerId }) => ({ ...state, activeLayerId: layerId })),

  on(FrameActions.renameLayer, (state, { layerId, name }) => ({
    ...state, layers: state.layers.map(l => l.id === layerId ? { ...l, name } : l),
  })),

  on(FrameActions.toggleLayerVisible, (state, { layerId }) => {
    const layers = state.layers.map(l => l.id === layerId ? { ...l, visible: !l.visible } : l);
    return { ...state, layers, frames: recompositeAll(state.frames, layers, state.cels) };
  }),

  on(FrameActions.toggleLayerLocked, (state, { layerId }) => ({
    ...state, layers: state.layers.map(l => l.id === layerId ? { ...l, locked: !l.locked } : l),
  })),

  on(FrameActions.toggleLayerContinuous, (state, { layerId }) => ({
    ...state, layers: state.layers.map(l => l.id === layerId ? { ...l, isContinuous: !l.isContinuous } : l),
  })),

  on(FrameActions.setLayerOpacity, (state, { layerId, opacity }) => {
    const layers = state.layers.map(l => l.id === layerId ? { ...l, opacity } : l);
    return { ...state, layers, frames: recompositeAll(state.frames, layers, state.cels) };
  }),

  on(FrameActions.setLayerBlend, (state, { layerId, blendMode }) => {
    const layers = state.layers.map(l => l.id === layerId ? { ...l, blendMode: blendMode as any } : l);
    return { ...state, layers, frames: recompositeAll(state.frames, layers, state.cels) };
  }),

  on(FrameActions.setLayerTint, (state, { layerId, tintColor }) => {
    const layers = state.layers.map(l => l.id === layerId ? { ...l, tintColor } : l);
    return { ...state, layers, frames: recompositeAll(state.frames, layers, state.cels) };
  }),

  on(FrameActions.setLayerHold, (state, { layerId, holdDuration }) => ({
    ...state, layers: state.layers.map(l => l.id === layerId ? { ...l, holdDuration } : l),
  })),

  on(FrameActions.moveLayerUp, (state, { layerId }) => {
    const idx = state.layers.findIndex(l => l.id === layerId);
    if (idx <= 0) return state;
    const layers = [...state.layers];
    [layers[idx - 1], layers[idx]] = [layers[idx], layers[idx - 1]];
    return { ...state, layers, frames: recompositeAll(state.frames, layers, state.cels) };
  }),

  on(FrameActions.moveLayerDown, (state, { layerId }) => {
    const idx = state.layers.findIndex(l => l.id === layerId);
    if (idx === -1 || idx >= state.layers.length - 1) return state;
    const layers = [...state.layers];
    [layers[idx], layers[idx + 1]] = [layers[idx + 1], layers[idx]];
    return { ...state, layers, frames: recompositeAll(state.frames, layers, state.cels) };
  }),

  on(FrameActions.mergeLayersDown, (state, { layerId }) => {
    const snap = snapshot(state);
    const idx = snap.layers.findIndex(l => l.id === layerId);
    if (idx <= 0) return snap;
    const upper = snap.layers[idx];
    const lower = snap.layers[idx - 1];
    const frameId = snap.currentFrameId;
    if (!frameId) return snap;
    const frame = snap.frames.find(f => f.id === frameId)!;
    const { w, h } = frameDims(frame);
    // Merge upper cel into lower cel for current frame only
    const upperPx = getCelPixels(snap.cels, frameId, upper.id, w, h);
    const lowerPx = getCelPixels(snap.cels, frameId, lower.id, w, h);
    const mergedPx = compositeLayers(
      [{ ...lower, pixels: lowerPx }, { ...upper, pixels: upperPx }],
      w, h
    );
    const layers = snap.layers.filter((_, i) => i !== idx);
    const cels = {
      ...Object.fromEntries(Object.entries(snap.cels).filter(([k]) => !k.endsWith('|' + upper.id))),
      [celKey(frameId, lower.id)]: mergedPx,
    };
    const frames = recompositeAll(snap.frames, layers, cels);
    return { ...snap, layers, cels, frames, activeLayerId: lower.id };
  }),

  // ── Collab-only ─────────────────────────────────────────────────────────────

  on(FrameActions.syncRemoteFullSync, (state, { frames, layers, cels, activeLayerId }) => ({
    ...state,
    frames,
    layers:        layers        ?? state.layers,
    cels:          cels          ?? state.cels,
    activeLayerId: activeLayerId !== undefined ? activeLayerId : state.activeLayerId,
    currentFrameId: frames[0]?.id ?? null,
    past: [], future: [],
  })),

  on(FrameActions.syncRemoteFrameDelete, (state, { frameId }) => {
    if (state.frames.length <= 1) return state;
    const idx = state.frames.findIndex(f => f.id === frameId);
    if (idx === -1) return state;
    const frames = state.frames.filter(f => f.id !== frameId);
    const prefix = frameId + '|';
    const cels = Object.fromEntries(Object.entries(state.cels).filter(([k]) => !k.startsWith(prefix)));
    const cur = state.currentFrameId === frameId
      ? (frames[Math.max(0, idx - 1)]?.id ?? frames[0].id)
      : state.currentFrameId;
    return { ...state, frames, cels, currentFrameId: cur };
  }),

  on(FrameActions.syncRemoteFrameOrder, (state, { frames }) => {
    const curStillExists = frames.some(f => f.id === state.currentFrameId);
    return {
      ...state,
      frames,
      currentFrameId: curStillExists ? state.currentFrameId : (frames[0]?.id ?? null),
    };
  }),

  on(FrameActions.syncRemoteLayers, (state, { layers, activeLayerId }) => {
    // Receive layer structure from peer.
    // Visibility and lock are local-only preferences — preserve the user's own
    // settings for any layer they already know about.  New layers (not yet in
    // local state) use the peer-supplied defaults (visible:true, locked:false).
    const localPrefs = new Map(state.layers.map(l => [l.id, { visible: l.visible, locked: l.locked }]));
    const mergedLayers = layers.map((l: any) => {
      const prefs = localPrefs.get(l.id);
      return prefs ? { ...l, visible: prefs.visible, locked: prefs.locked } : l;
    });
    // Only recomposite if cels are already loaded — if cels are empty the
    // composited frame pixels from syncRemoteFullSync would be wiped blank.
    const hasCels = Object.keys(state.cels).length > 0;
    const frames = hasCels ? recompositeAll(state.frames, mergedLayers, state.cels) : state.frames;
    return { ...state, layers: mergedLayers, activeLayerId, frames };
  }),

  on(FrameActions.syncRemoteCels, (state, { cels }) => {
    // Merge incoming cels and recomposite affected frames
    const merged = { ...state.cels, ...cels };
    // Find unique frame IDs from incoming cels
    const touchedFrameIds = new Set(Object.keys(cels).map(k => k.split('|')[0]));
    const frames = state.frames.map(f =>
      touchedFrameIds.has(f.id) ? recomposite(f, state.layers, merged) : f
    );
    return { ...state, cels: merged, frames };
  }),

  on(FrameActions.syncRemoteCelClear, (state, { celKeys }) => {
    // A remote peer's undo reverted a layer to blank — remove those cel entries
    // from our local map so we recomposite without their (now-undone) pixels.
    if (!celKeys.length) return state;
    const cels = { ...state.cels };
    let changed = false;
    const touchedFrameIds = new Set<string>();
    for (const key of celKeys) {
      if (key in cels) {
        delete cels[key];
        changed = true;
        const frameId = key.split('|')[0];
        if (frameId) touchedFrameIds.add(frameId);
      }
    }
    if (!changed) return state;
    const frames = state.frames.map(f =>
      touchedFrameIds.has(f.id) ? recomposite(f, state.layers, cels) : f
    );
    return { ...state, cels, frames };
  }),

  // ── Undo / Redo ──────────────────────────────────────────────────────────────

  on(FrameActions.undo, (state) => {
    if (!state.past.length) return state;
    const [prev, ...rest] = state.past;
    const current: FrameSnapshot = {
      frames: state.frames, layers: state.layers, cels: state.cels,
      activeLayerId: state.activeLayerId, currentFrameId: state.currentFrameId,
    };
    // Visibility and lock are user preferences, not undoable edits.
    // Carry the live values forward so undo never changes which layers are shown/locked.
    const visMap = new Map(state.layers.map(l => [l.id, { visible: l.visible, locked: l.locked }]));
    const restoredLayers = prev.layers.map(l => ({ ...l, ...(visMap.get(l.id) ?? {}) }));
    const restoredFrames = recompositeAll(prev.frames, restoredLayers, prev.cels);
    return { ...state, ...prev, layers: restoredLayers, frames: restoredFrames, past: rest, future: [current, ...state.future].slice(0, MAX_HISTORY) };
  }),

  on(FrameActions.redo, (state) => {
    if (!state.future.length) return state;
    const [next, ...rest] = state.future;
    const current: FrameSnapshot = {
      frames: state.frames, layers: state.layers, cels: state.cels,
      activeLayerId: state.activeLayerId, currentFrameId: state.currentFrameId,
    };
    // Same as undo: keep live visibility/lock state, don't let redo flip them.
    const visMap = new Map(state.layers.map(l => [l.id, { visible: l.visible, locked: l.locked }]));
    const restoredLayers = next.layers.map(l => ({ ...l, ...(visMap.get(l.id) ?? {}) }));
    const restoredFrames = recompositeAll(next.frames, restoredLayers, next.cels);
    return { ...state, ...next, layers: restoredLayers, frames: restoredFrames, future: rest, past: [current, ...state.past].slice(0, MAX_HISTORY) };
  }),

  // ── Collab-safe Undo / Redo ────────────────────────────────────────────────
  // Only restores cels for the layer that was active when the snapshot was taken.
  // Every other user's layer is left completely untouched, so an undo by one user
  // cannot inadvertently revert another user's paint strokes.

  on(FrameActions.undoCollab, (state) => {
    if (!state.past.length) return state;
    const [prev, ...rest] = state.past;
    const current: FrameSnapshot = {
      frames: state.frames, layers: state.layers, cels: state.cels,
      activeLayerId: state.activeLayerId, currentFrameId: state.currentFrameId,
    };
    // The layer that was active when this snapshot was taken is the one whose
    // cels were changed during the stroke — restore only those.
    const targetLayerId = prev.activeLayerId;
    const suffix = targetLayerId ? `|${targetLayerId}` : null;
    const mergedCels = { ...state.cels };
    if (suffix) {
      // Remove the live cels for that layer
      for (const key of Object.keys(mergedCels)) {
        if (key.endsWith(suffix)) delete mergedCels[key];
      }
      // Restore the pre-stroke cels for that layer from the snapshot
      for (const [key, val] of Object.entries(prev.cels)) {
        if (key.endsWith(suffix)) mergedCels[key] = val;
      }
    }
    // Bug-fix: also restore the layer structure when it changed between snapshots.
    // undoCollab is collab-safe for pixel strokes (only touches one layer's cels),
    // but layer add/delete affects the shared layer list and must be fully reverted.
    const layersStructureChanged =
      prev.layers.length !== state.layers.length ||
      prev.layers.some((l, i) => l.id !== state.layers[i]?.id);
    const restoredLayers = layersStructureChanged ? prev.layers : state.layers;
    const restoredActiveLayerId = layersStructureChanged ? prev.activeLayerId : state.activeLayerId;
    // When structure changed, also restore all cels from the snapshot so peers
    // receive a complete picture (e.g. a deleted layer's cels come back).
    const finalCels = layersStructureChanged ? { ...prev.cels } : mergedCels;
    const frames = recompositeAll(state.frames, restoredLayers, finalCels);
    return {
      ...state,
      layers: restoredLayers,
      activeLayerId: restoredActiveLayerId,
      cels: finalCels,
      frames,
      past: rest,
      future: [current, ...state.future].slice(0, MAX_HISTORY),
    };
  }),

  on(FrameActions.redoCollab, (state) => {
    if (!state.future.length) return state;
    const [next, ...rest] = state.future;
    const current: FrameSnapshot = {
      frames: state.frames, layers: state.layers, cels: state.cels,
      activeLayerId: state.activeLayerId, currentFrameId: state.currentFrameId,
    };
    const targetLayerId = next.activeLayerId;
    const suffix = targetLayerId ? `|${targetLayerId}` : null;
    const mergedCels = { ...state.cels };
    if (suffix) {
      for (const key of Object.keys(mergedCels)) {
        if (key.endsWith(suffix)) delete mergedCels[key];
      }
      for (const [key, val] of Object.entries(next.cels)) {
        if (key.endsWith(suffix)) mergedCels[key] = val;
      }
    }
    // Bug-fix: also restore layer structure on redo when it changed (mirrors undoCollab fix).
    const layersStructureChanged =
      next.layers.length !== state.layers.length ||
      next.layers.some((l, i) => l.id !== state.layers[i]?.id);
    const restoredLayers = layersStructureChanged ? next.layers : state.layers;
    const restoredActiveLayerId = layersStructureChanged ? next.activeLayerId : state.activeLayerId;
    const finalCels = layersStructureChanged ? { ...next.cels } : mergedCels;
    const frames = recompositeAll(state.frames, restoredLayers, finalCels);
    return {
      ...state,
      layers: restoredLayers,
      activeLayerId: restoredActiveLayerId,
      cels: finalCels,
      frames,
      past: [current, ...state.past].slice(0, MAX_HISTORY),
      future: rest,
    };
  }),

  // ── Persistence ──────────────────────────────────────────────────────────────

  on(FrameActions.loadFrames, (state) => ({ ...state, isLoading: true })),

  on(FrameActions.loadFramesSuccess, (state, { frames, layers, cels, activeLayerId, currentFrameId, past, future }) => ({
    ...state,
    frames:        frames.length ? frames : state.frames,
    layers:        layers.length ? layers : state.layers,
    cels:          cels ?? state.cels,
    activeLayerId: activeLayerId ?? state.activeLayerId,
    currentFrameId: currentFrameId ?? state.currentFrameId,
    past:   capHistory(past),
    future: capHistory(future),
    isLoading: false,
    error: null,
  })),

  on(FrameActions.loadFramesFailure, (state, { error }) => ({ ...state, isLoading: false, error })),
);
